import State from '@/state/State';
import { isRecognitionOnlyClaims } from '@/utils/scopedAdminGate';

export const isFlexPayLike = (claims = null) => {
  if (claims == null) {
    claims = State.state.claims;
  }
  return claims?.a || claims?.m || claims?.is;
};

// NB: Onboarding clients are gated to the /onboarding placeholder regardless of
// the company's partner_state. The intent of ROLE_ONBOARDING_CLIENT is "this
// person was seeded during onboarding and must NEVER be auto-upgraded to
// portal admin access" — even after the company is promoted to LIVE, they
// must contact support to be re-permissioned. The Onboarding page itself
// surfaces a different message once the company is no longer in ONBOARDING.
export const isOnboardingClient = (claims = null) => {
  if (claims == null) {
    claims = State.state.claims;
  }
  return Boolean(claims?.oc);
};

// Scoped (limited) admins the portal knows how to confine — currently only
// the recognition-only admin. Deliberately fail-closed: a future scope (e.g.
// payroll) is NOT admitted until its confinement gate ships alongside it.
export const isScopedAdmin = (claims = null) => {
  if (claims == null) {
    claims = State.state.claims;
  }
  return isRecognitionOnlyClaims(claims);
};

// A full portal admin (e.g. the support admin seeded for a company) whose
// company is still being onboarded. Such users default to the /onboarding
// pages as their landing page. Unlike isOnboardingClient this is driven by the
// company's partner_state, so it's "landing only" (no confinement) and releases
// automatically once the company goes LIVE.
export const isOnboardingCompanyAdmin = (claims = null, company = null) => {
  if (company == null) {
    company = State.state.company;
  }
  return Boolean(isFlexPayLike(claims) && company?.partner_state === 'ONBOARDING');
};

// Whether the portal confines this user to /onboarding. A plain onboarding
// client (oc, no admin) has no other portal access, so stays confined in every
// partner_state; a seeded support admin (oc + admin) is confined only while the
// company is still ONBOARDING, then falls back on admin access once it moves on.
// One rule backs the router gate, the sidebar and the tab so they can't drift.
export const isConfinedToOnboarding = (claims = null, company = null) => {
  if (claims == null) {
    claims = State.state.claims;
  }
  if (company == null) {
    company = State.state.company;
  }
  return isOnboardingClient(claims) && (!isFlexPayLike(claims) || company?.partner_state === 'ONBOARDING');
};
